import { useMemo, useRef, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { Spin } from 'antd';
import type { SelectProps } from 'antd';
import apiSearch from '@nxsys/services/search';
import { parseStyledText, stripLeadingWhitespace } from '@nxsys/utils/textUtils';
import { StyledAppSelect } from './index.styled';

type ApiSearchType = keyof typeof apiSearch;

export type OptionType = {
  label: string;
  value: string;
  [key: string]: any;
  id?: string | number;
};

export interface AppSelectSyncProps<ValueType = any>
  extends Omit<
    SelectProps<ValueType | ValueType[]>,
    'options' | 'children' | 'value' | 'onChange' | 'defaultValue'
  > {
  type: string;
  apiType: ApiSearchType;
  debounceTimeout?: number;
  onChangeValue?: (value: OptionType | OptionType[] | null) => void;
  value?: OptionType | OptionType[] | null;
  defaultValue?: OptionType | OptionType[] | null;
  isRefetchOnTouch?: boolean;
  paramFilter?: any;
  searchId?: string;
  onDataLoaded?: (options: OptionType[]) => void;
}

function AppSelectSync({
  // fetchOptions,
  debounceTimeout = 500,
  type,
  apiType,
  onChangeValue,
  value: controlledValue,
  defaultValue,
  isRefetchOnTouch = false,
  paramFilter,
  onDataLoaded,
  ...rest
}: AppSelectSyncProps) {
  const [fetching, setFetching] = useState(false);
  const [internalValue, setInternalValue] = useState<
    OptionType | OptionType[] | null
  >(defaultValue || null);
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const fetchRef = useRef(0);

  // Determine if component is controlled
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  // Update internal value when defaultValue changes (only for uncontrolled mode)
  useEffect(() => {
    if (!isControlled && defaultValue !== undefined) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, isControlled]);

  // Handle search with first letter capitalization and debounced fetching
  const handleSearch = (rawValue: string) => {
    const value = stripLeadingWhitespace(rawValue);
    if (value && value.length > 0) {
      const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
      setSearchValue(capitalizedValue);
    } else {
      setSearchValue(value);
    }
    // Trim trailing spaces for the API term only — they would match nothing,
    // but must stay visible in the input while typing multi-word searches
    debounceFetcher(value.trim());
  };

  // Custom filter function that works with capitalized search
  const customFilter = (input: string, option: any) => {
    const cleaned = input.trim();
    const capitalizedInput = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    return option?.label
      ?.toLowerCase()
      .includes(capitalizedInput.toLowerCase());
  };

  const getOptionLabel = (option: any) => {
    switch (type) {
      case 'emp&sub': {
        const name = option?.fullName
          ? option?.fullName
          : `${option?.forename || ''} ${option?.surname || ''}`;
        // NX-6290: show the SR id so two workers with the same name can be told
        // apart. It is part of the label on purpose: the local filterOption only
        // matches the label, so this also lets the user search by SR id.
        return option?.id ? `${name} (${option.id})` : name;
      }
      case 'agency':
        return `${option?.name || ''}`;
      case 'approver':
        return option?.forename && option?.surname
          ? `${option?.forename || ''} ${option?.surname || ''}`
          : option?.name;
      case 'VATCode':
        return `${option?.description || ''} (${option?.VATCode || ''}) - ${
          option?.VATRate || ''
        }`;
      case 'assignment':
      case 'employeeAssignment':
        return `${option?.assignmentName || ''}`;
      case 'paymentRate':
        return `${option?.code || ''}${option?.description ? '-' + option.description : ''}`;
      case 'agency&prospect':
      case 'agencyContact':
        return option?.name || '';
      case 'bankName':
        return option?.bankName || '';
      case 'company':
        return option?.companyName || '';
      case 'groupId':
        return option?.id || option?.name || '';
      case 'agencySite':
        return option?.id || '';
      case 'admin':
        return `${option?.forename || ''} ${option?.surname || ''}`;
      case 'expense':
        return option?.expenseName || '';
      default:
        return option?.toString() || '';
    }
  };

  const BoxOption = ({
    option,
    children,
  }: {
    option: any;
    children: React.ReactNode;
  }) => (
    <div
      key={option.id}
      style={{
        fontSize: '14px',
        fontWeight: 500,
        color: '#666666',
      }}
    >
      {children}
    </div>
  );

  const renderOption = (option: any) => {
    switch (type) {
      case 'emp&sub':
        return (
          <BoxOption option={option}>{`${option?.forename || ''} ${
            option?.surname || ''
          }${option?.id ? ` (${option.id})` : ''}`}</BoxOption>
        );
      case 'agency':
        return <BoxOption option={option}>{`${option?.name || ''}`}</BoxOption>;
      case 'approver':
        return (
          <BoxOption option={option}>
            {option?.forename && option?.surname
              ? `${option?.forename || ''} ${option?.surname || ''}`
              : option?.name}
          </BoxOption>
        );
      case 'VATCode':
        return (
          <BoxOption option={option}>{`${option?.description || ''} (${
            option?.VATCode || ''
          }) - ${option?.VATRate || ''}`}</BoxOption>
        );
      case 'assignment':
      case 'employeeAssignment':
        return (
          <BoxOption option={option}>{`${
            option?.assignmentName || ''
          }`}</BoxOption>
        );
      case 'paymentRate':
        return (
          <BoxOption option={option}>{`${option?.code || ''}-${
            option?.description || ''
          }`}</BoxOption>
        );
      case 'agency&prospect':
      case 'agencyContact':
        return <BoxOption option={option}>{`${option?.name || ''}`}</BoxOption>;
      case 'bankName':
        return <BoxOption option={option}>{option?.bankName || ''}</BoxOption>;
      case 'company':
        return (
          <BoxOption option={option}>{option?.companyName || ''}</BoxOption>
        );
      case 'groupId':
        return (
          <BoxOption option={option}>
            {option?.id || option?.name || ''}
          </BoxOption>
        );
      case 'agencySite':
        return (
          <BoxOption option={option}>{`${option?.siteName || ''}-${
            option?.id || ''
          }-${option?.code || ''}`}</BoxOption>
        );
      case 'admin':
        return (
          <BoxOption option={option}>
            {`${option?.forename || ''} ${option?.surname || ''}`}
          </BoxOption>
        );
      case 'expense':
        return (
          <BoxOption
            option={option}
          >{`${option?.expenseName || ''}`}</BoxOption>
        );
      default:
        return <div />;
    }
  };

  const debounceFetcher = useMemo(() => {
    async function fetchOptions(term: string): Promise<any[]> {
      return apiSearch[apiType](
        { search: term, page: 0, size: 20, ...paramFilter },
        rest?.searchId,
      ).then((res: any) => {
        const results = Array.isArray(res?.data.data) ? res?.data.data : [];
        return results;
      });
    }

    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }

        const mapped = newOptions.map((item: any) => ({
          label: getOptionLabel(item),
          value: item.id,
          data: item.id,
          ...item,
          original: item,
        }));
        setOptions(mapped);
        setFetching(false);
        onDataLoaded?.(mapped as OptionType[]);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [debounceTimeout, paramFilter, rest?.searchId, onDataLoaded]);

  // Fetch on mount when onDataLoaded is provided
  useEffect(() => {
    if (onDataLoaded) {
      debounceFetcher('');
    }
  }, []);

  const handleChange = (newValue: any) => {
    if (!isControlled) {
      setInternalValue(newValue as OptionType | OptionType[] | null);
    }

    if (onChangeValue) {
      newValue = newValue ?? null;
      // setValue(newValue as OptionType | OptionType[] | null);
      if (newValue && !Array.isArray(newValue)) {
        const fullData = options.find(
          (opt) => opt.value === (newValue as OptionType)?.value,
        );
        (newValue as { companyName?: string }).companyName =
          (fullData as any)?.companyPayment?.companyName || '';
        newValue.fullData = fullData;
      } else {
        newValue = newValue?.map((item: any) => {
          const fullData = options.find((opt) => opt.value === item.value);
          return {
            ...item,
            fullData: fullData,
          };
        });
      }
      onChangeValue(newValue as OptionType | OptionType[] | null);
    }
  };

  // Parse placeholder for styling if it contains ** markers
  const styledPlaceholder =
    rest.placeholder &&
    typeof rest.placeholder === 'string' &&
    rest.placeholder.includes('**')
      ? parseStyledText(rest.placeholder)
      : rest.placeholder;

  return (
    <StyledAppSelect
      showSearch
      labelInValue
      allowClear
      size="large"
      value={value}
      searchValue={searchValue}
      onSearch={handleSearch}
      filterOption={customFilter}
      notFoundContent={fetching ? <Spin size="small" /> : 'No results found'}
      {...rest}
      placeholder={styledPlaceholder}
      options={options}
      optionRender={(option: any) => renderOption(option.data)}
      onChange={handleChange}
      onOpenChange={(open) => {
        if (isRefetchOnTouch && open) {
          setFetching(true);
          setOptions([]);
          debounceFetcher('');
        } else {
          if (open && !options.length) {
            debounceFetcher('');
          }
        }
      }}
    />
  );
}

export default AppSelectSync;
